// import api's and mutations
import { queries } from 'RestAPIs/rapini';

// import components
import { Box } from '@mui/material';
import LoadingOverlay from 'components/shared/LoadingOverlay';

// import hooks
import { useEffect, useMemo, useState } from 'react';
import { RecoilRoot } from 'recoil';
import { useColumnDefinitions } from './useColumnDefinitions';

// import stores
import { useDateNavigationHoursStore } from 'components/shared/DateNavigation/DateNavigationHours.stores';

// import types
import { PlanUnitMix } from 'components/types';
import { AdditionalCompensationsProps } from './AdditionalCompensations.types';
import { AdditionalCompensationResponse } from 'generated/index';

// import utils
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import utc from 'dayjs/plugin/utc';
import { Header } from './Header';
import { useShallow } from 'zustand/shallow';
import { useDateNavigationStore } from 'components/shared/DateNavigation/DateNavigation.stores';
import { StyledTableCRUDLayout } from './AdditionalCompensations.styles';

dayjs.extend(utc);

const AdditionalCompensations = ({
  page = 'START',
  showEmployeeName = false
}: AdditionalCompensationsProps) => {
  const columns = useColumnDefinitions({ showEmployeeName });
  const [selectedPlanUnits, setSelectedPlanUnits] = useState<PlanUnitMix[]>([]);
  const {
    selectedDate: storeDate,
    selectedDateRange,
    setSelectedDate,
    setSelectedDateRange
  } = useDateNavigationHoursStore((state) => state);

  const { period, setPeriod } = useDateNavigationStore(
    useShallow((state) => state)
  );

  const isCustomDateRange = period === 'custom';
  const startDate = isCustomDateRange
    ? dayjs(selectedDateRange?.[0]).format('YYYY-MM-DD')
    : dayjs(Array.isArray(storeDate) ? storeDate[0] : storeDate)
        .startOf('month')
        .format('YYYY-MM-DD');
  const endDate = isCustomDateRange
    ? dayjs(selectedDateRange?.[1]).format('YYYY-MM-DD')
    : dayjs(Array.isArray(storeDate) ? storeDate[1] : storeDate)
        .endOf('month')
        .format('YYYY-MM-DD');

  const organizationalUnitIds =
    selectedPlanUnits &&
    Array.from(
      // create an unique set of costCenter ids
      new Set(
        selectedPlanUnits
          .map((unit) =>
            'costCenters' in unit &&
            Array.isArray(unit.costCenters) &&
            unit.costCenters?.length > 0
              ? unit.costCenters.map(({ id }) => id)
              : unit.id
          )
          .flat()
      )
    ).join(',');

  const isOverview = !page || page === 'START';
  const [shouldFetch, setShouldFetch] = useState(false);

  const getAdditionalCompensationsByOrganizationalUnits = () =>
    queries.useFindAdditionalCompensationsByOrganizationalUnits(
      startDate,
      endDate,
      // @ts-ignore
      organizationalUnitIds as string[],
      {
        enabled:
          shouldFetch &&
          !!organizationalUnitIds &&
          dayjs(startDate).isValid() &&
          dayjs(endDate).isValid()
      }
    );

  const { data = [], isLoading } =
    getAdditionalCompensationsByOrganizationalUnits();

  const rows = useMemo(
    () =>
      data.map((response: AdditionalCompensationResponse) => ({
        ...response,
        uniqueId: uuidv4()
      })),
    [data]
  );

  useEffect(() => {
    if (selectedPlanUnits.length > 0) setShouldFetch(true);
  }, [selectedPlanUnits, startDate, endDate]);

  useEffect(() => {
    if (isCustomDateRange) {
      setPeriod('custom');
    }
  }, [isCustomDateRange]);

  return (
    <RecoilRoot>
      <Header
        onSelectPlanUnit={setSelectedPlanUnits}
        setSelectedDateRange={setSelectedDateRange}
        setSelectedDate={setSelectedDate}
        period={isCustomDateRange ? 'custom' : 'month'}
        selectedDateRange={selectedDateRange}
        selectedDate={dayjs(startDate).toDate()}
        showPlanUnitSelector={isOverview}
        dateNavigationOptions={['week', 'month', 'custom']}
      />
      <Box>
        <StyledTableCRUDLayout
          rows={rows}
          getRowId={(row) => row?.uniqueId}
          columns={columns}
          loading={isLoading}
          pageSize={50}
          dynamicHeight={true}
          editable={false}
          hideFooter={rows?.length === 0}
          actionsThreshold={4}
          pageSizeOptions={[5]}
          initialState={{
            sorting: {
              sortModel: [{ field: 'mutatedOn', sort: 'desc' }]
            }
          }}
        />
      </Box>
      <LoadingOverlay loading={isLoading} />
    </RecoilRoot>
  );
};

export default AdditionalCompensations;
